import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import BadgeTier from "../components/BadgeTier";
import SEO from "../components/SEO";
import { InstallAppButton } from "../components/PWAInstall";
import { NEXUS_LOGO } from "../lib/api";
import Footer, { INSTAGRAM_URL, SUPPORT_EMAIL } from "../components/Footer";
import WorkflowShowcase from "../components/WorkflowShowcase";
import { CompetitorTable, NEXUS_SCORE, TOP3 } from "../components/CompetitorTable";
import CinematicHero from "../components/CinematicHero";
import Starfield from "../components/Starfield";
import { DealsTicker } from "../components/ResultsWall";
import HomePricing from "../components/HomePricing";
import {
  ShieldCheck,
  Sparkles,
  Wallet,
  Users,
  Search,
  Send,
  BadgeCheck,
  ArrowRight,
  Star,
  Smartphone,
  Download,
  Home as HomeIcon,
  Bell,
  MessageSquare,
  Check,
  Landmark,
  Zap,
} from "lucide-react";

const CREATOR_IMAGES = [
  "https://images.unsplash.com/photo-1508179522353-11ba468c4a1c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
  "https://images.unsplash.com/photo-1584359018585-82fb9b6b710d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
  "https://images.unsplash.com/photo-1533461502717-83546f485d24?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
  "https://images.unsplash.com/photo-1727334291061-fd29582ef9dc?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800",
];

/** Realistic in-phone app preview. Cycles between Discover → Campaign accept → Payout received. */
function PhoneMock() {
  const [screen, setScreen] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setScreen((s) => (s + 1) % 3), 3800);
    return () => clearInterval(id);
  }, []);

  const now = new Date();
  const clock = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;

  return (
    <div className="relative mx-auto flex items-center justify-center" data-testid="landing-phone-mock" style={{ minHeight: 640 }}>
      {/* Ambient glows */}
      <div className="absolute -top-8 -left-8 h-52 w-52 rounded-full bg-nexus-pink/25 blur-3xl -z-10" />
      <div className="absolute -bottom-8 -right-8 h-52 w-52 rounded-full bg-nexus-blue/25 blur-3xl -z-10" />

      {/* iPhone 15 Pro-style frame */}
      <div
        className="relative rounded-[52px] p-[3px] shadow-[0_40px_80px_rgba(0,0,0,0.65),0_0_0_1px_rgba(255,255,255,0.05)]"
        style={{
          width: 320,
          height: 640,
          background: "linear-gradient(160deg, #4a4a52 0%, #1c1c22 30%, #0a0a0e 60%, #1c1c22 100%)",
        }}
      >
        {/* Titanium bezel */}
        <div
          className="relative rounded-[49px] p-1.5 h-full w-full"
          style={{ background: "linear-gradient(160deg, #2a2a30 0%, #0a0a0e 100%)" }}
        >
          {/* Side buttons */}
          <div className="absolute -left-[3px] top-24 h-8 w-[3px] rounded-l bg-gradient-to-b from-[#3a3a40] to-[#1c1c22]" />
          <div className="absolute -left-[3px] top-40 h-14 w-[3px] rounded-l bg-gradient-to-b from-[#3a3a40] to-[#1c1c22]" />
          <div className="absolute -left-[3px] top-60 h-14 w-[3px] rounded-l bg-gradient-to-b from-[#3a3a40] to-[#1c1c22]" />
          <div className="absolute -right-[3px] top-32 h-20 w-[3px] rounded-r bg-gradient-to-b from-[#3a3a40] to-[#1c1c22]" />

          {/* Screen */}
          <div className="relative rounded-[42px] bg-[#060608] overflow-hidden h-full w-full">
            {/* Dynamic Island */}
            <div className="absolute top-2.5 left-1/2 -translate-x-1/2 h-[26px] w-[92px] bg-black rounded-full z-30 flex items-center justify-center gap-2 shadow-[0_0_0_1px_rgba(255,255,255,0.05)_inset]">
              <div className="h-1 w-1 rounded-full bg-white/20" />
            </div>

            {/* Status bar */}
            <div className="absolute top-1 inset-x-0 h-8 flex items-center justify-between px-6 z-20 text-[11px] text-white/95 font-semibold tabular-nums">
              <span>{clock}</span>
              <span className="flex items-center gap-1.5">
                {/* Signal dots */}
                <span className="flex items-end gap-[1.5px]">
                  <span className="h-1 w-[3px] bg-white rounded-[1px]" />
                  <span className="h-1.5 w-[3px] bg-white rounded-[1px]" />
                  <span className="h-2 w-[3px] bg-white rounded-[1px]" />
                  <span className="h-2.5 w-[3px] bg-white rounded-[1px]" />
                </span>
                <span className="text-[10px] font-medium">5G</span>
                <span className="inline-block h-[10px] w-[22px] border border-white/80 rounded-[3px] relative">
                  <span className="absolute -right-[3px] top-[2px] h-1 w-[2px] bg-white/80 rounded-r" />
                  <span className="absolute inset-[1px] bg-white/90 rounded-[1.5px]" />
                </span>
              </span>
            </div>

            {/* App content — three fading screens */}
            <div className="pt-11 pb-16 h-full relative">
              {/* Screen 1 — Discover */}
              <div className={`absolute inset-x-0 top-11 bottom-16 px-4 space-y-2.5 transition-opacity duration-500 ${screen === 0 ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <img src={NEXUS_LOGO} alt="" className="h-6 w-6 object-contain" />
                    <span className="font-display font-bold text-[13px]">NEXUS</span>
                  </div>
                  <Bell className="h-3.5 w-3.5 text-zinc-400" />
                </div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500">Discover creators</div>
                <div className="flex items-center gap-1.5 rounded-full bg-white/[0.04] hairline px-2.5 h-7">
                  <Search className="h-3 w-3 text-zinc-500" />
                  <span className="text-[9px] text-zinc-500">Fashion · UK · 10k+</span>
                </div>
                {CREATOR_IMAGES.slice(0, 3).map((src, i) => (
                  <div key={i} className="flex items-center gap-2 p-1.5 rounded-lg bg-white/[0.03] hairline">
                    <img src={src} alt="" className="h-9 w-9 rounded-md object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-semibold truncate flex items-center gap-1">
                        {["Ava M.", "Jules R.", "Nia K."][i]}
                        <BadgeCheck className="h-2.5 w-2.5 text-nexus-blue" />
                      </div>
                      <div className="text-[9px] text-zinc-500">Fashion · London</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[9px] font-display font-bold nexus-gradient-text">£{[250, 320, 180][i]}</div>
                      <div className="flex items-center gap-0.5 justify-end">
                        <Star className="h-2 w-2 text-yellow-300 fill-yellow-300" />
                        <span className="text-[8px] text-zinc-400 tabular-nums">{[4.9, 5.0, 4.8][i]}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Screen 2 — Campaign accepted */}
              <div className={`absolute inset-x-0 top-11 bottom-16 px-4 space-y-2.5 transition-opacity duration-500 ${screen === 1 ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <img src={NEXUS_LOGO} alt="" className="h-6 w-6 object-contain" />
                    <span className="font-display font-bold text-[13px]">NEXUS</span>
                  </div>
                  <Bell className="h-3.5 w-3.5 text-nexus-pink" />
                </div>
                <div className="rounded-xl p-2.5 bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2.5">
                  <div className="h-8 w-8 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
                    <Check className="h-3.5 w-3.5 text-emerald-300" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold text-emerald-200">Application accepted</div>
                    <div className="text-[9px] text-emerald-100/70 truncate">Streetwear brand · 3 street-style Reels</div>
                  </div>
                </div>
                <div className="rounded-xl p-2.5 bg-white/[0.03] hairline space-y-1.5">
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500">Escrow funded</div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-zinc-300">Brand pays</span>
                    <span className="font-display font-bold nexus-gradient-text text-base">£800</span>
                  </div>
                  <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full w-1/3 nexus-gradient rounded-full" />
                  </div>
                  <div className="text-[8px] text-zinc-500">In progress · 3 deliverables</div>
                </div>
                <div className="rounded-xl p-2.5 bg-white/[0.03] hairline">
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Next step</div>
                  <div className="text-[10px] text-zinc-200 leading-snug">Film & upload the first draft — brand reviews within 48h.</div>
                </div>
              </div>

              {/* Screen 3 — Payout received */}
              <div className={`absolute inset-x-0 top-11 bottom-16 px-4 space-y-2.5 transition-opacity duration-500 ${screen === 2 ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <img src={NEXUS_LOGO} alt="" className="h-6 w-6 object-contain" />
                    <span className="font-display font-bold text-[13px]">NEXUS</span>
                  </div>
                  <span className="text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-nexus-pink/15 border border-nexus-pink/40 text-nexus-pink">Pro</span>
                </div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500">Wallet</div>
                <div className="rounded-xl p-3 bg-gradient-to-br from-nexus-pink/20 via-nexus-purple/15 to-nexus-blue/20 border border-nexus-pink/30 relative overflow-hidden">
                  <div className="absolute top-2 right-2.5 h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <div className="text-[9px] uppercase tracking-widest text-zinc-300">Payout received</div>
                  <div className="font-display font-black text-2xl mt-0.5 tabular-nums">£800.00</div>
                  <div className="text-[9px] text-zinc-400">100% of the £800 brief · zero creator fees</div>
                </div>
                <div className="rounded-lg p-2 bg-white/[0.03] hairline flex items-center gap-2">
                  <div className="h-7 w-7 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
                    <Landmark className="h-3 w-3 text-emerald-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-semibold flex items-center gap-1">
                      Barclays ••8842
                      <Check className="h-2.5 w-2.5 text-emerald-300" />
                    </div>
                    <div className="text-[8px] text-zinc-500">Ref NX-CP-2A87F</div>
                  </div>
                  <span className="text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Paid</span>
                </div>
                <div className="rounded-lg p-2 bg-white/[0.03] hairline flex items-center gap-2">
                  <div className="h-7 w-7 rounded-md bg-nexus-blue/15 border border-nexus-blue/30 flex items-center justify-center shrink-0">
                    <Zap className="h-3 w-3 text-nexus-blue" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-semibold">+120 XP · Level up</div>
                    <div className="text-[8px] text-zinc-500">Silver badge unlocked</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom tab bar */}
            <div className="absolute bottom-0 inset-x-0 h-14 border-t border-white/5 bg-black/80 backdrop-blur flex items-center justify-around z-20">
              {[
                { i: HomeIcon,      k: 0 },
                { i: MessageSquare, k: 1 },
                { i: Wallet,        k: 2 },
                { i: Users,         k: 0 },
              ].map((t, i) => {
                const Icon = t.i;
                const on = t.k === screen;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setScreen(t.k)}
                    className={`flex items-center justify-center h-8 w-8 rounded-full transition-colors ${on ? "text-nexus-pink" : "text-zinc-500 hover:text-zinc-300"}`}
                    aria-label={`Show screen ${t.k + 1}`}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                );
              })}
            </div>

            {/* Screen indicator dots */}
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex items-center gap-1 z-20">
              {[0, 1, 2].map((i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setScreen(i)}
                  className={`h-1 rounded-full transition-all ${i === screen ? "w-4 bg-nexus-pink" : "w-1 bg-white/25 hover:bg-white/50"}`}
                  aria-label={`Go to screen ${i + 1}`}
                  data-testid={`phone-dot-${i}`}
                />
              ))}
            </div>

            {/* Home indicator */}
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 h-1 w-24 bg-white/25 rounded-full z-20" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen grain relative">
      <Starfield />
      <div className="relative z-10">
      <SEO
        title="Hire Vetted UK Creators | Escrow-Protected"
        description="UK creator marketplace with escrow on every deal. Brands hire vetted TikTok, Instagram and YouTube creators; funds are held until content is approved."
        path="/"
        keywords={[
          "hire influencers UK",
          "creator collab platform",
          "TikTok creator hire",
          "Instagram creator marketplace",
          "UGC creator platform",
          "brand-creator escrow",
          "paid collaborations",
          "content creator hire",
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Nexus",
          "description": "Escrow-protected marketplace connecting brands with vetted social media creators.",
          "logo": NEXUS_LOGO,
          "email": SUPPORT_EMAIL,
          "sameAs": [INSTAGRAM_URL],
        }}
      />
      <Navbar />

      <CinematicHero />

      {/* RESULTS WALL */}
      <DealsTicker />

      {/* HOW IT WORKS DETAIL */}
      <WorkflowShowcase />

      {/* WHY NEXUS vs competitors */}
      <section className="px-6 lg:px-10 py-20 border-t border-white/5" data-testid="landing-compare-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Why Nexus</div>
          <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tighter mb-3">How Nexus compares with Influee, Insense and Superfiliate.</h2>
          <p className="text-zinc-400 mb-8 max-w-2xl">Only claims we can point to on each platform's own pricing, terms and FAQ pages. No subscription to start, creators keep 100%, payouts within hours. Score: Nexus {NEXUS_SCORE}.</p>
          <CompetitorTable showWhy columns={TOP3} />
          <Link to="/trust" className="inline-flex items-center gap-2 mt-6 text-sm text-nexus-blue hover:underline" data-testid="landing-compare-link">Full comparison and sources <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      <HomePricing />

      {/* TRUST SECTION */}
      <section className="px-6 lg:px-10 py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <div className="space-y-6">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">
              Built-in trust layer
            </div>
            <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight">
              Every profile earns its <span className="nexus-gradient-text">reputation</span>.
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed max-w-xl">
              Every creator gets a public reputation score computed from real reviews across
              five categories: Quality, Communication, Creativity, Delivery Speed, and
              Professionalism. Badge tiers unlock as track record grows.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <BadgeTier tier="bronze" />
              <BadgeTier tier="silver" />
              <BadgeTier tier="gold" />
              <BadgeTier tier="platinum" />
              <BadgeTier tier="elite" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: ShieldCheck, k: "100%", v: "Escrow-backed hires" },
              { icon: BadgeCheck, k: "7-step", v: "Verification stack" },
              { icon: Star, k: "5-factor", v: "Reputation scoring" },
              { icon: Sparkles, k: "0%", v: "Ghost payments" },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.v} className="p-6 rounded-2xl bg-[#0C0C10] hairline card-hover">
                  <Icon className="h-5 w-5 text-nexus-blue mb-4" />
                  <div className="font-display font-black text-4xl tracking-tight nexus-gradient-text">{s.k}</div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mt-2">{s.v}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GET THE APP */}
      <section className="px-6 lg:px-10 py-20 border-t border-white/5" id="get-the-app">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <div className="space-y-6">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
              <Smartphone className="h-3.5 w-3.5" /> Mobile app
            </div>
            <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tighter leading-tight">
              Take Nexus <span className="nexus-gradient-text">everywhere</span> — no App Store required.
            </h2>
            <p className="text-zinc-400 max-w-xl leading-relaxed">
              Install Nexus straight from your browser as a fully-fledged mobile app.
              Push-ready, offline-friendly and one tap from your home screen. No downloads,
              no updates, no waiting.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <InstallAppButton className="inline-flex items-center gap-2 nexus-gradient text-white font-semibold rounded-full px-6 h-12 hover:opacity-90" />
              <a href="#top" className="inline-flex items-center gap-2 bg-white/5 border border-white/15 text-white hover:bg-white/10 rounded-full px-6 h-12 text-sm font-semibold transition-colors">
                <Download className="h-4 w-4" /> See how it works
              </a>
            </div>
            <div className="grid grid-cols-3 gap-4 pt-4">
              {[
                { k: "1-tap", v: "Home screen install" },
                { k: "Offline", v: "Repeat visits load instantly" },
                { k: "0 MB", v: "No App Store download" },
              ].map((s) => (
                <div key={s.v} className="p-3 rounded-xl bg-[#0C0C10] hairline text-center">
                  <div className="font-display font-black nexus-gradient-text text-xl">{s.k}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Phone mock */}
          <PhoneMock />
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 lg:px-10 py-24 border-t border-white/5">
        <div className="max-w-5xl mx-auto glass rounded-3xl p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-nexus-pink/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-nexus-blue/20 blur-3xl" />
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div className="max-w-xl">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-3">Ready to start?</div>
              <h3 className="font-display font-bold text-3xl md:text-4xl tracking-tight leading-tight">
                Fund your first collab in{" "}
                <span className="nexus-gradient-text">two minutes.</span>
              </h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/discover">
                <Button
                  className="nexus-gradient text-white font-semibold rounded-full px-7 h-12 hover:opacity-90 border-0"
                  data-testid="cta-browse-btn"
                >
                  Browse creators
                </Button>
              </Link>
              <Link to="/auth?mode=signup">
                <Button
                  variant="outline"
                  className="bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white rounded-full px-7 h-12"
                  data-testid="cta-signup-btn"
                >
                  Create account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer>
        <Footer />
      </footer>
      </div>
    </div>
  );
}
